import React, { useState } from "react";
import { useT } from "@/i18n/LanguageContext";
import SectionHeading from "@/components/SectionHeading";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import api from "@/lib/api";
import { Flag, AlertTriangle, ShieldCheck } from "lucide-react";

const verdictColor = (s) =>
  s >= 70 ? "text-[#FF3B30] border-[#FF3B30]/40 bg-[#FF3B30]/10" :
  s >= 45 ? "text-[#FF6A60] border-[#FF6A60]/40 bg-[#FF3B30]/5" :
  s >= 20 ? "text-[#D4AF37] border-[#D4AF37]/40 bg-[#D4AF37]/5" :
            "text-[#10B981] border-[#10B981]/40 bg-[#10B981]/5";

const RedFlags = () => {
  const { t, lang } = useT();
  const [state, setState] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggle = (k) => setState((s) => ({ ...s, [k]: !s[k] }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/red-flags", state);
      setResult(res.data);
      setTimeout(() => document.getElementById("rf-result")?.scrollIntoView({ behavior: "smooth", block: "center" }), 120);
    } finally {
      setLoading(false);
    }
  };

  const flaggedCount = Object.values(state).filter(Boolean).length;

  return (
    <div className="mx-auto max-w-4xl px-4 md:px-8 py-16 md:py-24" data-testid="redflags-page">
      <SectionHeading eyebrow="Behavioural Audit" title={t.redFlag.title} subtitle={t.redFlag.subtitle} />

      <form onSubmit={submit} className="mt-10 surface-card p-6 md:p-8 space-y-3" data-testid="redflag-form">
        {t.redFlag.checklist.map((c) => (
          <label
            key={c.key}
            htmlFor={`rf-${c.key}`}
            className={`flex items-start gap-3 rounded-lg border px-4 py-3 cursor-pointer transition-colors ${
              state[c.key] ? "border-[#FF3B30]/60 bg-[#FF3B30]/5" : "border-white/10 bg-white/[0.02] hover:bg-white/[0.04]"
            }`}
            data-testid={`rf-${c.key}`}
          >
            <Checkbox
              id={`rf-${c.key}`}
              checked={!!state[c.key]}
              onCheckedChange={() => toggle(c.key)}
              className="mt-1 border-white/30 data-[state=checked]:bg-[#FF3B30] data-[state=checked]:border-[#FF3B30]"
            />
            <span className={`text-sm leading-relaxed ${state[c.key] ? "text-white" : "text-white/85"} ${lang === "hi" ? "lang-hi" : ""}`}>
              <Flag className={`inline-block h-3.5 w-3.5 mr-2 ${state[c.key] ? "text-[#FF3B30]" : "text-white/40"}`} />
              {c.label}
            </span>
          </label>
        ))}

        <div className="pt-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="text-xs text-white/55 font-mono-alt uppercase tracking-widest">
            {flaggedCount} {lang === "hi" ? "चयनित" : "selected"}
          </div>
          <Button type="submit" disabled={loading} className="bg-[#FF3B30] hover:bg-[#cc2e25] text-white font-bold gap-2 h-11 px-6" data-testid="rf-calculate-btn">
            <AlertTriangle className="h-4 w-4" />
            {loading ? t.common.loading : (lang === "hi" ? "स्कोर की गणना करें" : "Calculate Risk Score")}
          </Button>
        </div>
      </form>

      {result && (
        <div id="rf-result" className={`mt-8 fade-up rounded-xl border ${verdictColor(result.score)} p-6 md:p-8`} data-testid="rf-result">
          <div className="flex items-center gap-4">
            {result.score >= 45 ? <AlertTriangle className="h-10 w-10" /> : <ShieldCheck className="h-10 w-10" />}
            <div>
              <div className="text-xs uppercase tracking-widest opacity-70 font-mono-alt">{t.redFlag.score}</div>
              <div className="font-display text-2xl md:text-3xl font-black tracking-tight">{result.verdict}</div>
            </div>
          </div>
          <div className="mt-6 flex items-center gap-4">
            <div className="flex-1 h-2 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${result.score}%`, background: result.score >= 70 ? "#FF3B30" : result.score >= 45 ? "#FF6A60" : result.score >= 20 ? "#D4AF37" : "#10B981" }} />
            </div>
            <div className="font-mono-alt text-2xl font-bold">{result.score}</div>
          </div>
          <div className="mt-4 text-xs opacity-75">
            {t.redFlag.flagged}: {result.flagged.length}
          </div>
        </div>
      )}
    </div>
  );
};

export default RedFlags;
